import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useUI } from '@/store/ui'
import { ApiError, DiwaAPI } from '@/api/client'
import { DomainConflictError, DomainsAPI, type Domain, type DomainDraft } from '@/api/domains'
import { SharingAPI } from '@/api/sharing'
import { useDomainSelection } from '@/store/domainSelection'
import { HelpHint, Legend } from '@/components/HelpHint'
import { DomainActivity } from '@/components/ActivityHeatmap'
import { DomainBuilder } from '@/pages/Domains'
import { TalaRefine } from '@/components/TalaRefine'
import { mergeEgoMap, mergeTypes, type TalaSuggestion } from '@/lib/talaRefine'
import { toTagSlug } from '@/lib/sphereShelves'

// Control-plane content for the Domains page: inspect the selected domain (info + delete), or create.

interface DomainOverview {
  slug: string
  description: string
  vocabulary: { entity_types: string[]; relation_types: string[]; words: Record<string, string> }
  springs: { name: string; status?: string; last_run?: string }[]
  has_rules: boolean
  counts: { entities_by_type: Record<string, number>; entities_total: number; signals_total: number; signals_fresh: number }
}

const SPRING_GLYPH: Record<string, string> = { ok: '●', late: '◐', failing: '✕', rate_limited: '◔' }

/** The "what did I get" panel — the manifest made legible (P9-T01). */
function OverviewSection({ domainId }: { domainId: string }) {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new DiwaAPI(diwaUrl), [diwaUrl])
  const { data } = useQuery({
    queryKey: ['domain-overview', domainId],
    queryFn: () => api.get<DomainOverview>(`/domains/${domainId}/overview`),
  })
  if (!data) return null
  return (
    <>
      <div>
        <div className="dw-rail-section-title">What lives here</div>
        <div style={{ display: 'grid', gap: 4, fontSize: 'var(--ae-font-size-xs)' }}>
          {data.vocabulary.entity_types.map((t) => {
            // The server walks the gloss chain (domain words → type words → category vocab →
            // generic); the client keeps the honest floor so no type EVER renders blank.
            const word = data.vocabulary.words[t] || 'an entity this domain tracks'
            return (
              <div key={t}>
                <HelpHint gloss={word}><strong>{t}</strong></HelpHint>
                {data.counts.entities_by_type[t] != null && (
                  <span className="dw-inbox-detail"> · {data.counts.entities_by_type[t]}</span>
                )}
                <span className="dw-inbox-detail"> — {word}</span>
              </div>
            )
          })}
        </div>
      </div>
      <div>
        <div className="dw-rail-section-title">
          What feeds it <HelpHint term="spring" />
        </div>
        {data.springs.length > 0 && <Legend family="spring" className="dw-legend-springs" />}
        {data.springs.length === 0 ? (
          <div className="dw-rail-empty">No springs bundled — evidence arrives by your hand (Trove, capture).</div>
        ) : (
          <div style={{ display: 'grid', gap: 4, fontSize: 'var(--ae-font-size-xs)' }}>
            {data.springs.map((sp) => (
              <div key={sp.name}>
                <span className={`dw-pipeline-status dw-pipeline-status--${sp.status ?? 'never_ran'}`}>
                  {SPRING_GLYPH[sp.status ?? ''] ?? '○'}
                </span>{' '}
                {sp.name}
                {sp.last_run && (
                  <span className="dw-inbox-detail"> · last {new Date(sp.last_run).toLocaleString()}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <div className="dw-rail-section-title">Its pulse</div>
        <div className="dw-inbox-detail">
          {data.counts.entities_total} stars · {data.counts.signals_total} signals
          {data.counts.signals_fresh > 0 && ` (${data.counts.signals_fresh} fresh)`}
          {' · '}
          {data.has_rules
            ? 'signal rules configured'
            : 'no signal rules yet — the matcher is silent for this domain'}
        </div>
      </div>
    </>
  )
}

/** Pipe any live spring into this domain — per-user, honest about ties. */
function SpringAttach({ domain, onSaved }: { domain: Domain; onSaved: (d: Domain) => void }) {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new DomainsAPI(diwaUrl), [diwaUrl])
  const plain = useMemo(() => new DiwaAPI(diwaUrl), [diwaUrl])
  const qc = useQueryClient()
  const { data: fleet } = useQuery({
    queryKey: ['pipelines'],
    queryFn: () => plain.get<{ pipelines: { name: string }[] }>('/pipelines'),
    staleTime: 60_000,
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const attached = (domain.attached_springs ?? []) as string[]
  const options = (fleet?.pipelines ?? []).map((p) => p.name).filter((n) => !attached.includes(n))

  async function save(next: string[]) {
    setBusy(true)
    setError(null)
    try {
      const updated = await api.update(domain.id, { attached_springs: next })
      onSaved(updated)
      // the cached domains list carries attached_springs too — leaving it stale would
      // let a later re-select + save silently revert this change
      await qc.invalidateQueries({ queryKey: ['domains'] })
      await qc.invalidateQueries({ queryKey: ['domain-overview', domain.id] })
    } catch {
      setError("Couldn't update the springs — nothing changed. Try again.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <div className="dw-rail-section-title">
        <HelpHint term="spring">Springs</HelpHint> feeding this domain
      </div>
      {attached.length === 0 && (
        <div className="dw-inbox-detail" style={{ marginBottom: 6 }}>None attached — pipe one in below.</div>
      )}
      <div style={{ display: 'grid', gap: 4, marginBottom: 6 }}>
        {attached.map((name) => (
          <div key={name} style={{ fontSize: 'var(--ae-font-size-xs)' }}>
            ● {name}{' '}
            <button type="button" className="dw-inline-link" disabled={busy} aria-label={`detach ${name}`}
                    onClick={() => save(attached.filter((n) => n !== name))}>
              detach
            </button>
          </div>
        ))}
      </div>
      {options.length > 0 && attached.length < 12 && (
        <select className="dw-input" disabled={busy} value="" aria-label="Attach a spring"
                onChange={(e) => { if (e.target.value) save([...attached, e.target.value]) }}>
          <option value="">+ attach a spring…</option>
          {options.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      )}
      {error && <div className="dw-error" style={{ marginTop: 4 }}>{error}</div>}
    </div>
  )
}


// Tags mirror the backend's law: ≤12 per domain, each a lowercase kebab slug of ≤24 chars
// (the slugifier itself lives in lib/sphereShelves — shared with the builder's tags field).
const TAG_MAX = 12

/** Tag the domain — ChipInput-style (Domains.tsx's isn't exported), each add/remove
 *  writing straight through PATCH so the chips shown are always the saved truth. */
function TagsEditor({ domain, onSaved }: { domain: Domain; onSaved: (d: Domain) => void }) {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new DomainsAPI(diwaUrl), [diwaUrl])
  const qc = useQueryClient()
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const tags = domain.tags ?? []

  async function save(next: string[]) {
    setBusy(true)
    setError(null)
    try {
      const updated = await api.update(domain.id, { tags: next })
      onSaved(updated)
      // the cached domains list carries tags too — leaving it stale would let a
      // later re-select show tags that no longer exist
      await qc.invalidateQueries({ queryKey: ['domains'] })
    } catch {
      setError("Couldn't save the tags — nothing changed. Try again.")
    } finally {
      setBusy(false)
    }
  }

  function add() {
    const tag = toTagSlug(draft)
    if (!tag || busy) return
    setDraft('')
    if (tags.includes(tag) || tags.length >= TAG_MAX) return
    void save([...tags, tag])
  }

  return (
    <div>
      <div className="dw-rail-section-title">Tags</div>
      <div className="dw-chipinput">
        {tags.map((tag) => (
          <button
            key={tag}
            type="button"
            className="dw-chip dw-chip--removable"
            title={`remove ${tag}`}
            disabled={busy}
            onClick={() => void save(tags.filter((t) => t !== tag))}
          >
            {tag}
            <span aria-hidden="true">×</span>
          </button>
        ))}
        {tags.length < TAG_MAX && (
          <input
            className="dw-chipinput-field"
            value={draft}
            disabled={busy}
            placeholder={tags.length === 0 ? 'tag this domain, Enter adds' : ''}
            aria-label="Add a tag"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                add()
              }
            }}
          />
        )}
      </div>
      {error && <div className="dw-error" style={{ marginTop: 4 }}>{error}</div>}
    </div>
  )
}


/** "✦ Ask Tala to refine" on the EDIT path — same affordance as the builder, scoped to the
 *  domain being edited. Applied chips write straight through PATCH (like TagsEditor), so the
 *  vocabulary shown is always the saved truth; a failed PATCH surfaces in the component and
 *  changes nothing. */
function RefineWithTala({ domain, onSaved }: { domain: Domain; onSaved: (d: Domain) => void }) {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new DomainsAPI(diwaUrl), [diwaUrl])
  const qc = useQueryClient()

  async function apply(suggestions: TalaSuggestion[]) {
    const draft: DomainDraft = {}
    const ents = suggestions.filter((s) => s.kind === 'entity').map((s) => s.name)
    const edges = suggestions.filter((s) => s.kind === 'edge').map((s) => s.name)
    const egos = suggestions.filter((s) => s.kind === 'ego')
    if (ents.length > 0) draft.entity_types = mergeTypes(domain.entity_types, ents)
    if (edges.length > 0) draft.relation_types = mergeTypes(domain.relation_types, edges)
    if (egos.length > 0) draft.ego_relations = mergeEgoMap(domain.ego_relations ?? {}, egos)
    if (Object.keys(draft).length === 0) return
    const updated = await api.update(domain.id, draft)
    onSaved(updated)
    // the cached list + overview carry the vocabulary too — stale copies would revert
    await qc.invalidateQueries({ queryKey: ['domains'] })
    await qc.invalidateQueries({ queryKey: ['domain-overview', domain.id] })
  }

  return (
    <div>
      <div className="dw-rail-section-title">Refine with Tala</div>
      <TalaRefine
        surface="domains"
        domainName={domain.name}
        entityTypes={domain.entity_types}
        relationTypes={domain.relation_types}
        egoNames={Object.keys(domain.ego_relations ?? {})}
        onApply={apply}
      />
    </div>
  )
}


const ROLE_HELP: Record<string, string> = {
  viewer: 'sees the domain',
  contributor: 'proposes at your gate',
  editor: 'writes, attributed',
}

/** Members + invite links — one domain, two worlds. Owner-only rail section. */
function MembersRail({ domain }: { domain: Domain }) {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new SharingAPI(diwaUrl), [diwaUrl])
  const qc = useQueryClient()
  const { data } = useQuery({
    queryKey: ['domain-members', domain.id],
    queryFn: () => api.members(domain.id),
  })
  const [role, setRole] = useState('viewer')
  const [busy, setBusy] = useState(false)
  const [link, setLink] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function mint() {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const made = await api.mintInvite(domain.id, role)
      setLink(`${window.location.origin}/invite/${made.token}`)
      await qc.invalidateQueries({ queryKey: ['domain-members', domain.id] })
    } catch (err) {
      const status = (err as { status?: number }).status
      const detail = (err as { detail?: string }).detail
      setError(status === 402 ? (detail ?? 'Sharing is a paid capability — upgrade to invite members.')
                              : "Couldn't mint the invite — try again.")
    } finally {
      setBusy(false)
    }
  }

  async function drop(kind: 'member' | 'invite', id: string) {
    setError(null)
    try {
      if (kind === 'member') await api.removeMember(domain.id, id)
      else await api.revokeInvite(domain.id, id)
      await qc.invalidateQueries({ queryKey: ['domain-members', domain.id] })
    } catch {
      setError("Couldn't remove that — try again.")
    }
  }

  const members = data?.members ?? []
  const invites = data?.invites ?? []

  return (
    <div>
      <div className="dw-rail-section-title">Members</div>
      {members.length === 0 && invites.length === 0 && (
        <div className="dw-inbox-detail" style={{ marginBottom: 6 }}>
          Yours alone — mint an invite link to share this domain.
        </div>
      )}
      <div style={{ display: 'grid', gap: 4, marginBottom: 6 }}>
        {members.map((m) => (
          <div key={m.id} style={{ fontSize: 'var(--ae-font-size-xs)' }}>
            ● {m.email ?? 'member'}
            <span className="dw-inbox-detail"> · {m.role}</span>{' '}
            <button type="button" className="dw-inline-link" aria-label={`remove ${m.email ?? 'member'}`}
                    onClick={() => drop('member', m.id)}>
              remove
            </button>
          </div>
        ))}
        {invites.map((i) => (
          <div key={i.id} className="dw-inbox-detail" style={{ fontSize: 'var(--ae-font-size-xs)' }}>
            ○ open link {i.prefix}… · {i.role}{' '}
            <button type="button" className="dw-inline-link" aria-label={`revoke invite ${i.prefix}`}
                    onClick={() => drop('invite', i.id)}>
              revoke
            </button>
          </div>
        ))}
      </div>
      {link && (
        <div className="dw-rail-empty" style={{ marginBottom: 6, fontSize: 'var(--ae-font-size-xs)' }}>
          Send this link — it will not be shown again:{' '}
          <code style={{ fontFamily: 'var(--ae-font-mono)', userSelect: 'all', wordBreak: 'break-all' }}>{link}</code>{' '}
          <button type="button" className="dw-inline-link" onClick={() => setLink(null)}>done</button>
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <select className="dw-input" value={role} aria-label="Invite role"
                onChange={(e) => setRole(e.target.value)} style={{ width: 130 }}>
          {Object.entries(ROLE_HELP).map(([r, help]) => (
            <option key={r} value={r}>{r} — {help}</option>
          ))}
        </select>
        <button type="button" className="dw-btn" disabled={busy} onClick={mint}>
          {busy ? 'Minting…' : '+ invite link'}
        </button>
      </div>
      {error && <div className="dw-error" style={{ marginTop: 4, fontSize: 'var(--ae-font-size-xs)' }}>{error}</div>}
    </div>
  )
}


/** A shared domain's world, plus the write affordance a member's role grants:
 *  editor writes directly, contributor proposes at the owner's gate, viewer reads. */
function RegionContent({ domain }: { domain: Domain }) {
  const { diwaUrl } = useUI()
  const plain = useMemo(() => new DiwaAPI(diwaUrl), [diwaUrl])
  const api = useMemo(() => new SharingAPI(diwaUrl), [diwaUrl])
  const qc = useQueryClient()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['region-content', domain.id],
    queryFn: () => plain.get<{ entities: { id: string; name: string; type: string; created_by: string | null }[] }>(`/domains/${domain.id}/region`),
  })
  const canWrite = domain.role === 'editor' || domain.role === 'contributor'
  const [name, setName] = useState('')
  const [type, setType] = useState(domain.entity_types[0] ?? '')
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function add() {
    if (!name.trim() || !type || busy) return
    setBusy(true)
    setError(null)
    setNote(null)
    try {
      const r = await api.writeEntity(domain.id, { type, name: name.trim() })
      setName('')
      setNote(r.proposed ? 'Proposed — waiting at the owner’s gate.' : 'Added to the domain.')
      if (r.applied) await qc.invalidateQueries({ queryKey: ['region-content', domain.id] })
    } catch {
      setError("Couldn't add that — try again.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <div className="dw-rail-section-title">What lives here</div>
      {isLoading && <div className="dw-empty">Loading the domain…</div>}
      {isError && <div className="dw-error">Couldn't read the domain — try again.</div>}
      {data && data.entities.length === 0 && (
        <div className="dw-inbox-detail">Nothing held here yet.</div>
      )}
      <div style={{ display: 'grid', gap: 3, marginBottom: canWrite ? 8 : 0 }}>
        {(data?.entities ?? []).slice(0, 30).map((e) => (
          <div key={e.id} style={{ fontSize: 'var(--ae-font-size-xs)' }}>
            ✳ {e.name} <span className="dw-inbox-detail">· {e.type}{e.created_by ? ' · added by a member' : ''}</span>
          </div>
        ))}
      </div>
      {data && data.entities.length > 30 && (
        <div className="dw-inbox-detail" style={{ marginTop: 4 }}>…and {data.entities.length - 30} more</div>
      )}
      {canWrite && (
        <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
          <div className="dw-inbox-detail">
            {domain.role === 'editor'
              ? 'You can add to this domain — writes are attributed to you.'
              : 'You can propose additions — they wait at the owner’s gate.'}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <select className="dw-input" value={type} aria-label="Entity type" style={{ width: 110 }}
                    onChange={(e) => setType(e.target.value)}>
              {domain.entity_types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <input className="dw-input" placeholder="name" value={name} style={{ flex: 1 }}
                   aria-label="Entity name" onChange={(e) => setName(e.target.value)} />
            <button type="button" className="dw-btn" disabled={busy || !name.trim() || !type} onClick={add}>
              {busy ? '…' : domain.role === 'editor' ? 'Add' : 'Propose'}
            </button>
          </div>
          {note && <div className="dw-inbox-detail">{note}</div>}
          {error && <div className="dw-error" style={{ fontSize: 'var(--ae-font-size-xs)' }}>{error}</div>}
        </div>
      )}
    </div>
  )
}


/** Curated public publish toggle — a 403 means this account isn't on the launch allowlist. */
function PublishToggle({ domain, onChanged }: { domain: Domain; onChanged: (d: Domain) => void }) {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new SharingAPI(diwaUrl), [diwaUrl])
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const published = !!domain.published

  async function toggle() {
    if (busy) return
    setBusy(true)
    setNote(null)
    try {
      await api.setPublished(domain.id, !published)
      onChanged({ ...domain, published: !published })
    } catch (err) {
      const status = (err as { status?: number }).status
      setNote(status === 403 ? 'Publishing is curated during launch.' : "Couldn't change publish state.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <button className="dw-action-btn" disabled={busy} onClick={toggle}>
        {published ? 'Unpublish (make private)' : 'Publish as public showcase'}
      </button>
      {published && (
        <div className="dw-inbox-detail" style={{ fontSize: 'calc(11px * var(--ae-text-scale, 1))' }}>
          Live at <a className="dw-link" href={`/r`} target="_blank" rel="noreferrer">/r</a> — view-only,
          anonymous; nothing personal is shown.
        </div>
      )}
      {note && <div className="dw-inbox-detail" style={{ fontSize: 'calc(11px * var(--ae-text-scale, 1))' }}>{note}</div>}
    </>
  )
}


export function DomainInspect() {
  const { diwaUrl } = useUI()
  const api = new DomainsAPI(diwaUrl)
  const qc = useQueryClient()
  const { selected, select } = useDomainSelection()
  const [busy, setBusy] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [draftName, setDraftName] = useState('')
  const [actionErr, setActionErr] = useState<string | null>(null)

  if (!selected) return <div className="dw-rail-empty">Click a domain card to inspect it.</div>
  const isSystem = selected.type === 'system'
  const isArchived = selected.status === 'archived'

  async function del() {
    if (isSystem || busy || !selected) return
    setBusy(true)
    try {
      await api.remove(selected.id)
      select(null)
      await qc.invalidateQueries({ queryKey: ['domains'] })
    } finally {
      setBusy(false)
    }
  }

  async function rename() {
    if (isSystem || busy || !selected || !draftName.trim()) return
    setBusy(true)
    setActionErr(null)
    try {
      const updated = await api.update(selected.id, { name: draftName.trim() })
      select(updated)
      setRenaming(false)
      await qc.invalidateQueries({ queryKey: ['domains'] })
    } catch {
      setActionErr('Rename failed — the domain may have changed elsewhere. Try again.')
    } finally {
      setBusy(false)
    }
  }

  async function toggleArchive() {
    if (isSystem || busy || !selected) return
    setBusy(true)
    setActionErr(null)
    try {
      const updated = await api.update(selected.id, { status: isArchived ? 'active' : 'archived' })
      select(updated)
      await qc.invalidateQueries({ queryKey: ['domains'] })
    } catch {
      setActionErr(`${isArchived ? 'Unarchive' : 'Archive'} failed — try again.`)
    } finally {
      setBusy(false)
    }
  }

  async function exportDomain() {
    if (busy || !selected) return
    setBusy(true)
    setActionErr(null)
    try {
      const bundle = await new DiwaAPI(diwaUrl).get<unknown>(`/domains/${selected.id}/export`)
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${selected.slug}-export.json`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      setActionErr('Export failed — could not fetch the bundle.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <OverviewSection domainId={selected.id} />
      <DomainActivity slug={selected.slug} />
      <div>
        <div
          className="dw-rail-section-title"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}
        >
          <span>Domain</span>
          {/* the explicit door — same affordance as the domain cards' footer link */}
          <Link
            to={`/domains/${selected.slug}`}
            className="dw-inline-link"
            title={`open ${selected.name}'s page`}
          >
            Open page →
          </Link>
        </div>
        <div className="dw-field-grid">
          <div className="dw-field-label">Name</div>
          <div className="dw-field-value">
            {renaming ? (
              <span style={{ display: 'inline-flex', gap: 4 }}>
                <input
                  className="dw-input"
                  value={draftName}
                  autoFocus
                  onChange={(e) => setDraftName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void rename(); if (e.key === 'Escape') setRenaming(false) }}
                />
                <button className="dw-btn" disabled={busy || !draftName.trim()} onClick={rename}>Save</button>
              </span>
            ) : (
              <>
                {selected.name}
                {!isSystem && !selected.shared && (
                  <button
                    type="button"
                    className="dw-inline-link"
                    style={{ marginLeft: 6, fontSize: 'calc(11px * var(--ae-text-scale, 1))' }}
                    onClick={() => { setDraftName(selected.name); setRenaming(true) }}
                  >
                    rename
                  </button>
                )}
              </>
            )}
          </div>
          <div className="dw-field-label">Type</div>
          <div className="dw-field-value">{selected.type}</div>
          {(selected.tags ?? []).length > 0 && (
            <>
              <div className="dw-field-label">Tags</div>
              <div className="dw-field-value">
                <span className="dw-chip-row" style={{ opacity: 0.7 }}>
                  {(selected.tags ?? []).map((t) => (
                    <span key={t} className="dw-chip">{t}</span>
                  ))}
                </span>
              </div>
            </>
          )}
          <div className="dw-field-label">Status</div>
          <div className="dw-field-value">{selected.status}</div>
        </div>
        {selected.description && (
          <div style={{ fontSize: 'calc(11.5px * var(--ae-text-scale, 1))', color: 'var(--ae-color-text-secondary)', marginTop: 8, lineHeight: 1.5 }}>
            {selected.description}
          </div>
        )}
      </div>

      <div>
        <div className="dw-rail-section-title">Entity types</div>
        <div className="dw-chip-row">
          {selected.entity_types.map((t) => (
            <span key={t} className="dw-chip">{t}</span>
          ))}
        </div>
      </div>

      {!isSystem && !selected.shared && <RefineWithTala domain={selected} onSaved={(d) => select(d)} />}
      {!isSystem && !selected.shared && <TagsEditor domain={selected} onSaved={(d) => select(d)} />}
      {!isSystem && !selected.shared && <SpringAttach domain={selected} onSaved={(d) => select(d)} />}
      {selected.shared ? <RegionContent domain={selected} /> : <MembersRail domain={selected} />}

      <div>
        <div className="dw-rail-section-title">Relation types</div>
        <div className="dw-chip-row">
          {selected.relation_types.map((t) => (
            <span key={t} className="dw-chip" style={{ opacity: 0.8 }}>{t}</span>
          ))}
        </div>
      </div>

      {selected.shared ? (
        <div className="dw-rail-empty">Shared by {selected.owner} — you hold it as {selected.role}. Reading grants no hands.</div>
      ) : isSystem ? (
        <div className="dw-rail-empty">System domain — can't be edited or deleted.</div>
      ) : (
        <div style={{ display: 'grid', gap: 6 }}>
          <button className="dw-action-btn" disabled={busy} onClick={exportDomain}>Export domain (JSON)</button>
          <button className="dw-action-btn" disabled={busy} onClick={toggleArchive}>
            {isArchived ? 'Unarchive domain' : 'Archive domain'}
          </button>
          <div className="dw-inbox-detail" style={{ fontSize: 'calc(11px * var(--ae-text-scale, 1))' }}>
            {isArchived
              ? 'Archived: springs are paused for this domain. Unarchive to resume signals.'
              : 'Archiving pauses its springs — no new signals. Your stars stay exactly as they are.'}
          </div>
          <PublishToggle domain={selected} onChanged={(d) => select(d)} />
          <button className="dw-action-btn" disabled={busy} onClick={del}>Delete domain</button>
          {actionErr && (
            <div style={{ fontSize: 'calc(11px * var(--ae-text-scale, 1))', color: 'var(--ae-color-feedback-error)' }}>{actionErr}</div>
          )}
        </div>
      )}
    </div>
  )
}

/** The create tab hosts the SAME guided builder as the page CTA — one create flow
 *  everywhere (founder ruling, Domains surface pass). The old template gallery is gone;
 *  templates fork from their cards on the Domains page, and the old "custom region"
 *  path survives only as the builder's "Custom domain (advanced / manual)" toggle. */
export function DomainCreate() {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new DomainsAPI(diwaUrl), [diwaUrl])
  const qc = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [nameError, setNameError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [createdName, setCreatedName] = useState<string | null>(null)
  // bumping the key resets the builder to a blank slate after a create or cancel
  const [nonce, setNonce] = useState(0)

  async function create(draft: DomainDraft) {
    setBusy(true)
    setNameError(null)
    setFormError(null)
    try {
      const made = await api.create(draft)
      await qc.invalidateQueries({ queryKey: ['domains'] })
      setCreatedName(made.name)
      setNonce((n) => n + 1)
    } catch (err) {
      setCreatedName(null)
      if (err instanceof DomainConflictError) setNameError(err.detail)
      else if (err instanceof ApiError && err.status === 402)
        setFormError(err.detail ?? 'Upgrade your plan to add more domains.')
      else setFormError("Couldn't create the domain. Try again.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="dw-builder--compact">
      <div className="dw-rail-section-title">New domain</div>
      {createdName && (
        <div className="dw-inbox-detail" style={{ marginBottom: 8 }}>
          “{createdName}” created ✓ — its springs and rules are listed in its overview.
        </div>
      )}
      <DomainBuilder
        key={nonce}
        submitLabel="Create domain"
        busy={busy}
        nameError={nameError}
        formError={formError}
        onSubmit={create}
        onCancel={() => {
          setNameError(null)
          setFormError(null)
          setCreatedName(null)
          setNonce((n) => n + 1)
        }}
      />
    </div>
  )
}
